<script setup>
import {ref} from "vue";
import axios from "axios";
import Swal from 'sweetalert2';
import {axiosCatchHandler} from "../../modules/sharedGlobal";
import DisableTwoFactorModal from "./DisableTwoFactorModal.vue";
import RegenerateCodesModal from "./RegenerateCodesModal.vue";

const emit = defineEmits(['update:recoveryCodes', 'disabled']);

const props = defineProps({
  recoveryCodes: {
    required: false,
    type: Array,
    default: () => ([]),
  },
  showRecoveryCodes: {
    required: false,
    type: Boolean,
    default: false,
  },
});

const loading = ref(false);
const showRecoveryCodes = ref(props.showRecoveryCodes);
const showTwoFactorDisableModal = ref(false);
const showRecoveryCodesModal = ref(false);
const recoveryCodes = ref(props.recoveryCodes);

const disableErrors = ref([]);
const regenerateErrors = ref([]);

/**
 * The endpoints verify the code themselves, so a wrong one comes back as a
 * validation error. Keep the modal open and show it against the field rather
 * than closing and leaving the user to guess what happened.
 */
const handleCodeError = (error, errors) => {
  axiosCatchHandler(error, ({response}) => {
    if (response.status === 422 && response.data.errors && response.data.errors.code) {
      errors.value = response.data.errors.code;

      return;
    }

    Swal.fire({
      title: "Something went wrong",
      html: (response.data && response.data.message) || 'Please try again.',
      icon: 'error',
    });
  });
}

const handleRegenerateRecoveryCodes = async (code) => {
  loading.value = true;
  regenerateErrors.value = [];

  try {
    const {data} = await axios.post('/two-factor/regenerate', {code})
    recoveryCodes.value = data.recoveryCodes;
    loading.value = false;
    showRecoveryCodesModal.value = false;

    emit('update:recoveryCodes', recoveryCodes.value);
  } catch (error) {
    loading.value = false;
    handleCodeError(error, regenerateErrors);
  }
}

const handleDisable = async (code) => {
  loading.value = true;
  disableErrors.value = [];

  try {
    await axios.post('/two-factor/disable', {code});
    loading.value = false;
    showTwoFactorDisableModal.value = false;
    recoveryCodes.value = {};

    emit('disabled');
  } catch (error) {
    loading.value = false;
    handleCodeError(error, disableErrors);
  }
}
</script>

<template>
  <div class="parent-container">
    <div class="text-center two-factor-container mw-100">
      <div
        class="alert-success text-success border-radius-6 py-2 d-flex justify-content-center align-items-center flex-wrap"
      >
        <svg-vue
          class="svg mr-0 mr-lg-2"
          icon="icons/tick-green"
        />
        <span class="font-weight-bold mx-2 mx-lg-0">
          Two-factor authentication enabled
        </span>
      </div>

      <p
        v-if="showRecoveryCodes"
        class="letter-spacing my-4"
      >
        Store these recovery codes in a secure password manager. They can be used to recover access to your
        account if your two factor authentication device is lost.
      </p>
    </div>

    <div class="d-flex justify-content-between flex-column my-2">
      <div>
        <div
          v-if="showRecoveryCodes"
          class="recovery-code-block p-4 p-md-5 mb-4"
        >
          <p
            v-for="(code, index) in recoveryCodes"
            :key="index"
            class="font-weight-bold letter-spacing mb-3 mb-md-1"
          >
            {{ code }}
          </p>
        </div>

        <hr v-show="!showRecoveryCodes">
      </div>

      <div class="d-flex justify-content-between align-items-md-center flex-column flex-md-row">
        <button
          class="btn show-recovery-codes-btn btn-lg button flex-shrink-1 font-weight-bold mb-2 mb-md-0 mr-0 mr-lg-2"
          :disabled="loading"
          @click="showRecoveryCodes = !showRecoveryCodes"
        >
          {{ showRecoveryCodes ? 'Hide Recovery Codes' : 'Show Recovery Codes' }}
        </button>

        <div class="d-flex flex-column flex-lg-row">
          <button
            v-if="showRecoveryCodes"
            class="btn btn-info btn-lg button flex-shrink-1 font-weight-bold mb-lg-0 mb-2 mr-0 mr-lg-2"
            :disabled="loading"
            @click="showRecoveryCodesModal = true"
          >
            Regenerate Recovery Codes
          </button>
          <RegenerateCodesModal
            v-if="showRecoveryCodesModal"
            :errors="regenerateErrors"
            :loading="loading"
            @close="showRecoveryCodesModal = false"
            @confirm="handleRegenerateRecoveryCodes"
          />

          <button
            class="btn btn-danger btn-lg button flex-shrink-1 font-weight-bold m-0"
            :disabled="loading"
            @click="showTwoFactorDisableModal = true"
          >
            Disable Two-Factor Authentication
          </button>
          <DisableTwoFactorModal
            v-if="showTwoFactorDisableModal"
            :errors="disableErrors"
            :loading="loading"
            @close="showTwoFactorDisableModal = false"
            @confirm="handleDisable"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.parent-container {
  margin: 0 auto;
  max-width: 850px;
}

.show-recovery-codes-btn {
  background: #C7C4C1;
  color: white;
}

.two-factor-container {
  width: 40rem;
  margin: 0 auto;
}

.recovery-code-block {
  background: #F8F7F5;
  column-count: 2;
  column-gap: 20px;
  border-radius: 12px;
  text-align: center;
}

@media (max-width: 768px) {
  .recovery-code-block {
    column-count: 1;
  }
}

.btn-danger {
  background: #F34F4F;
}

.letter-spacing {
  letter-spacing: 0.72px;
}

.svg {
  max-width: 32px !important;
}
</style>
